import { TaskStatus, type Task } from '../hooks/useTaskManager';
import { extractAndCacheInlineMedia, clearAllInlineMediaCache } from './taskMediaCache';

// ---------------- 类型定义 ----------------
// 缓存的任务结构，携带写入时间，方便做 TTL 和排序控制
export interface CachedTask {
  task: Task;
  cachedAt: number;
}

// localStorage 中保存的完整缓存结构
export interface TaskCache {
  version: number;
  tasks: CachedTask[];
}

// ---------------- 常量区 ----------------
const CACHE_STORAGE_KEY = 'iarts_sync_tasks_cache';
const CACHE_VERSION = 1;
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 小时有效期
const CACHE_CAPACITY = 100; // 最多保存 100 条

// ---------------- 工具方法 ----------------
const getSafeStorage = (): Storage | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const hasMeaningfulRequestId = (value: unknown): boolean => {
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  return Boolean(value);
};

const isFinalStatus = (status: unknown): status is TaskStatus =>
  status === TaskStatus.SUCCESS || status === TaskStatus.FAILED;

const shouldCacheTask = (task: Task | null | undefined): task is Task => {
  if (!task) {
    return false;
  }
  if (!isFinalStatus(task.status)) {
    return false;
  }
  if (hasMeaningfulRequestId(task.requestId)) {
    return false;
  }
  return Boolean(task.id);
};

type CacheRecordLike = {
  cachedAt?: unknown;
  task?: unknown;
};

type TaskLike = Partial<Task> & Record<string, unknown>;

const isValidTaskShape = (value: unknown): value is Task => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const candidate = value as TaskLike;
  if (typeof candidate.id !== 'string' || !candidate.id.trim()) {
    return false;
  }
  if (typeof candidate.model !== 'string' || !candidate.model.trim()) {
    return false;
  }
  if (typeof candidate.createdAt !== 'number' || !Number.isFinite(candidate.createdAt)) {
    return false;
  }
  if (typeof candidate.updatedAt !== 'number' || !Number.isFinite(candidate.updatedAt)) {
    return false;
  }
  if (!isFinalStatus(candidate.status)) {
    return false;
  }
  if (hasMeaningfulRequestId(candidate.requestId)) {
    return false;
  }
  return true;
};

const MAX_RESULT_STRING_LENGTH = 40_000;
const MAX_RESULT_DEPTH = 8;
const MAX_RESULT_ARRAY_LENGTH = 50;
// payload 中字符串的最大长度（用于裁剪 base64 等大型数据）
const MAX_PAYLOAD_STRING_LENGTH = 500;

/**
 * 清理缓存数据中的大型字符串（如 base64 编码的数据）
 * @param value 需要清理的值
 * @param depth 当前递归深度
 * @param maxStringLength 字符串最大长度
 */
const sanitizeForCache = (value: unknown, depth = 0, maxStringLength = MAX_RESULT_STRING_LENGTH): unknown => {
  if (value === null || value === undefined) return value;

  // 先处理原子类型，避免因深度限制丢失真实文本内容
  if (typeof value === 'string') {
    if (value.length > maxStringLength) {
      // base64 数据或 data URL
      if (value.startsWith('data:') || /^[A-Za-z0-9+/=]{100,}$/.test(value.slice(0, 200))) {
        return `[binary-data:${Math.round(value.length / 1024)}KB]`;
      }
      return `${value.slice(0, maxStringLength)}...[truncated-${value.length - maxStringLength} chars]`;
    }
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (value instanceof Date) return value.toISOString();

  if (depth > MAX_RESULT_DEPTH) return '[MaxDepth]';

  if (Array.isArray(value)) {
    const trimmed = value.slice(0, MAX_RESULT_ARRAY_LENGTH).map((item) => sanitizeForCache(item, depth + 1, maxStringLength));
    if (value.length > MAX_RESULT_ARRAY_LENGTH) {
      trimmed.push(`[+${value.length - MAX_RESULT_ARRAY_LENGTH} more]`);
    }
    return trimmed;
  }

  if (typeof value === 'object') {
    const result: Record<string, unknown> = {};
    Object.entries(value as Record<string, unknown>).forEach(([key, val]) => {
      result[key] = sanitizeForCache(val, depth + 1, maxStringLength);
    });
    return result;
  }

  return String(value);
};

/**
 * 清理 result 字段用于缓存（保留较多文本内容）
 */
const sanitizeResultForCache = (value: unknown, depth = 0): unknown => {
  return sanitizeForCache(value, depth, MAX_RESULT_STRING_LENGTH);
};

/**
 * 清理 payload 字段用于缓存（严格裁剪，移除 base64 等大型数据）
 */
const sanitizePayloadForCache = (payload: unknown): unknown => {
  if (!payload || typeof payload !== 'object') return payload;

  // 对 payload 使用更严格的字符串长度限制
  return sanitizeForCache(payload, 0, MAX_PAYLOAD_STRING_LENGTH);
};

/**
 * 同步版本的快照创建（用于从缓存读取时的规范化，不提取媒体）
 */
const createCacheSnapshotSync = (task: Task): Task => {
  const { requestId, ...restTask } = task;
  const snapshot: Task = { ...(restTask as Task) };

  if (task.result !== undefined) {
    snapshot.result = sanitizeResultForCache(task.result) as Task['result'];
  }

  if (task.payload !== undefined) {
    snapshot.payload = sanitizePayloadForCache(task.payload) as Task['payload'];
  }

  return snapshot;
};

/**
 * 异步版本的快照创建（用于写入缓存时，会先提取 inline base64 到 IndexedDB）
 */
const createCacheSnapshot = async (task: Task): Promise<Task> => {
  // 排除 requestId，确保仅缓存真正的同步任务
  // 注意：缓存的目的是刷新后展示任务列表，不用于重新执行任务
  // 因此 payload 中的 base64 数据会被裁剪以节省 localStorage 空间
  const { requestId, ...restTask } = task;
  const snapshot: Task = { ...(restTask as Task) };

  // 先将 result 中的 inline base64 数据提取到 IndexedDB
  // 这样 sanitizeResultForCache 就不会遇到大型 base64 字符串
  if (task.result !== undefined) {
    const extractedResult = await extractAndCacheInlineMedia(task.id, task.result);
    snapshot.result = sanitizeResultForCache(extractedResult) as Task['result'];
  }

  // 清理 payload 字段（严格裁剪，移除 base64 等大型数据）
  if (task.payload !== undefined) {
    snapshot.payload = sanitizePayloadForCache(task.payload) as Task['payload'];
  }

  return snapshot;
};

const sortAndTrimRecords = (records: CachedTask[]): CachedTask[] => {
  const sorted = [...records].sort((a, b) => a.cachedAt - b.cachedAt);
  if (sorted.length > CACHE_CAPACITY) {
    sorted.splice(0, sorted.length - CACHE_CAPACITY);
  }
  return sorted;
};

const normalizeCacheRecords = (rawRecords: unknown[], now: number): CachedTask[] => {
  const normalized: CachedTask[] = [];
  for (const record of rawRecords) {
    if (!record || typeof record !== 'object') continue;
    const { cachedAt, task } = record as CacheRecordLike;
    if (typeof cachedAt !== 'number' || !Number.isFinite(cachedAt)) continue;
    if (!task || !isValidTaskShape(task)) continue;
    if (now - cachedAt > CACHE_TTL) continue;
    // 使用同步版本，因为读取时数据已经被处理过了
    normalized.push({ cachedAt, task: createCacheSnapshotSync(task) });
  }
  return sortAndTrimRecords(normalized);
};

const readCacheRecords = (storage: Storage): CachedTask[] => {
  try {
    const raw = storage.getItem(CACHE_STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw) as TaskCache | null;
    if (!parsed || parsed.version !== CACHE_VERSION || !Array.isArray(parsed.tasks)) {
      return [];
    }
    return normalizeCacheRecords(parsed.tasks, Date.now());
  } catch (error) {
    console.warn('[taskSyncCache] Failed to load cache', error);
    return [];
  }
};

const persistCacheRecords = (storage: Storage, records: CachedTask[]): void => {
  try {
    const payload: TaskCache = {
      version: CACHE_VERSION,
      tasks: sortAndTrimRecords(records)
    };
    storage.setItem(CACHE_STORAGE_KEY, JSON.stringify(payload));
  } catch (error) {
    console.warn('[taskSyncCache] Failed to persist cache', error);
  }
};

// ---------------- 对外函数 ----------------
/**
 * 读取同步任务缓存并自动过滤过期/非法数据
 */
export const loadSyncTasksCache = (): Task[] => {
  const storage = getSafeStorage();
  if (!storage) {
    return [];
  }
  const records = readCacheRecords(storage);
  // 为了让 localStorage 中的数据也保持最新排序/TTL，我们在读取后尝试写回
  try {
    persistCacheRecords(storage, records);
  } catch {
    // 忽略写回失败
  }
  return records.map((record) => record.task);
};

/**
 * 将单个同步任务写入缓存：仅允许 SUCCESS/FAILED 且无 requestId 的任务进入缓存
 * 注意：此函数是异步的，因为需要将 inline base64 数据提取到 IndexedDB
 */
export const saveSyncTaskToCache = async (task: Task): Promise<void> => {
  const storage = getSafeStorage();
  if (!storage || !shouldCacheTask(task)) {
    return;
  }
  try {
    const existingRecords = readCacheRecords(storage);
    const records = existingRecords.filter((record) => record.task.id !== task.id);
    const cachedAt = Date.now();
    // 使用异步版本提取 inline base64 到 IndexedDB
    const snapshot = await createCacheSnapshot(task);
    records.push({ cachedAt, task: snapshot });
    persistCacheRecords(storage, records);
  } catch (error) {
    console.warn('[taskSyncCache] Failed to save task', error);
  }
};

/**
 * 清空同步任务缓存（包括 localStorage 和 IndexedDB 中的媒体数据）
 */
export const clearSyncTaskCache = async (): Promise<void> => {
  const storage = getSafeStorage();
  if (!storage) {
    return;
  }
  try {
    storage.removeItem(CACHE_STORAGE_KEY);
    // 同时清理 IndexedDB 中的 inline 媒体缓存
    await clearAllInlineMediaCache();
  } catch (error) {
    console.warn('[taskSyncCache] Failed to clear cache', error);
  }
};
